import axios from "../api/axios";
import { useEffect, useState } from "react";

function getOrCreateDeviceId() {
  const existing = localStorage.getItem("deviceId");
  if (existing) return existing;

  const id =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  localStorage.setItem("deviceId", id);
  return id;
}

// Email validation
function isValidEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

// Password validation - Strong password requirements
function isValidPassword(password) {
  if (!password || password.length < 10) {
    return { valid: false, message: "Password must be at least 10 characters long" };
  }

  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[[\]!@#$%^&*(),.?":{}|<>_\-+=\\/`'~]/.test(password);

  if (!hasUpperCase) {
    return { valid: false, message: "Password must contain at least one uppercase letter" };
  }
  if (!hasLowerCase) {
    return { valid: false, message: "Password must contain at least one lowercase letter" };
  }
  if (!hasNumber) {
    return { valid: false, message: "Password must contain at least one number" };
  }
  if (!hasSpecial) {
    return { valid: false, message: "Password must contain at least one special character" };
  }

  return { valid: true, message: "" };
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");
  const [accountLocked, setAccountLocked] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpNewPasswordConfirm, setFpNewPasswordConfirm] = useState("");
  const [fpStep, setFpStep] = useState(1);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState("");
  const [fpSuccess, setFpSuccess] = useState("");

  // DISABLED: Auto-redirect causing infinite refresh
  // useEffect(() => {
  //   const token = sessionStorage.getItem("token");
  //   const role = sessionStorage.getItem("role")?.toUpperCase();
  //   if (token && role) {
  //     window.location.href = role === "ADMIN" ? "/admin/dashboard" : "/client/dashboard";
  //   }
  // }, []);

  // Clear totp and lock when email/password changes
  useEffect(() => {
    setNeedsTotp(false);
    setTotp("");
    setError("");
    setAccountLocked(false);
  }, [email, password]);

  // Clear errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validateLoginForm = () => {
    if (!email.trim()) {
      setError("Email address is required");
      return false;
    }
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!password) {
      setError("Password is required");
      return false;
    }
    if (needsTotp && !totp.trim()) {
      setError("Please enter your 6-digit authenticator code");
      return false;
    }
    if (needsTotp && totp.trim().length !== 6) {
      setError("Authenticator code must be exactly 6 digits");
      return false;
    }
    return true;
  };

  const login = async () => {
    if (accountLocked) {
      setError("Account is temporarily locked. Please try again later.");
      return;
    }

    if (!validateLoginForm()) {
      return;
    }

    setLoggingIn(true);
    setError("");

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        password,
        deviceId: getOrCreateDeviceId(),
        deviceName: typeof navigator !== "undefined" ? navigator.userAgent : "",
      };
      if (needsTotp) payload.totp = totp.trim();

      const res = await axios.post("/auth/login", payload);

      if (res.data && res.data.token) {
        const rawRole = res.data.user?.role || res.data.role;
        const formattedRole = String(rawRole || "CLIENT").toUpperCase();
        const tenantId = res.data.user?.id ? String(res.data.user.id) : "";

        // Save in both sessionStorage (per-tab isolation) and localStorage
        sessionStorage.setItem("token", res.data.token);
        sessionStorage.setItem("role", formattedRole);
        if (tenantId) sessionStorage.setItem("userId", tenantId);

        localStorage.setItem("token", res.data.token);
        localStorage.setItem("role", formattedRole);
        if (tenantId) localStorage.setItem("userId", tenantId);
        localStorage.removeItem("serverBootId");

        // Redirect after brief delay for smooth transition
        setTimeout(() => {
          if (formattedRole === "ADMIN") {
            window.location.href = "/admin/dashboard";
          } else {
            window.location.href = "/client/dashboard";
          }
        }, 300);
      }
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 429) {
        setAccountLocked(true);
        setError(data?.message || "Too many login attempts. Please try again later.");
        return;
      }

      if (status === 401 && data?.requires2fa) {
        setNeedsTotp(true);
        setError("A 6-digit code from your authenticator app is required to sign in.");
        return;
      }

      if (status === 401) {
        setError(data?.message || "Invalid email or password. Please check and try again.");
      } else if (status === 400) {
        setError(data?.message || "Invalid credentials provided.");
      } else if (status === 500) {
        setError("Server error. Please try again later.");
      } else {
        setError(data?.message || "Sign in failed. Please try again.");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const validateResetForm = () => {
    if (!fpEmail.trim()) {
      setFpError("Email address is required");
      return false;
    }
    if (!isValidEmail(fpEmail)) {
      setFpError("Please enter a valid email address");
      return false;
    }
    if (fpStep === 2) {
      if (!fpOtp.trim() || fpOtp.trim().length !== 6) {
        setFpError("OTP must be exactly 6 digits");
        return false;
      }
      if (!fpNewPassword) {
        setFpError("New password is required");
        return false;
      }
      const passwordCheck = isValidPassword(fpNewPassword);
      if (!passwordCheck.valid) {
        setFpError(passwordCheck.message);
        return false;
      }
      if (fpNewPassword !== fpNewPasswordConfirm) {
        setFpError("Passwords do not match");
        return false;
      }
    }
    return true;
  };

  const sendOtp = async () => {
    setFpError("");
    setFpSuccess("");

    if (!validateResetForm()) {
      return;
    }

    setFpLoading(true);

    try {
      await axios.post("/otp/generate", { email: fpEmail.trim().toLowerCase(), type: "EMAIL" });
      setFpSuccess("OTP sent to your email. Please check your inbox.");
      setFpStep(2);
    } catch (err) {
      setFpError(err?.response?.data?.message || "Failed to send OTP. Please check the email and try again.");
    } finally {
      setFpLoading(false);
    }
  };

  const resetPassword = async () => {
    setFpError("");
    setFpSuccess("");

    if (!validateResetForm()) {
      return;
    }

    setFpLoading(true);

    try {
      await axios.post(
        "/otp/verify",
        {
          email: fpEmail.trim().toLowerCase(),
          otp: fpOtp.trim(),
          new_password: fpNewPassword,
        }
      );
      setFpSuccess("Password reset successful! Redirecting to login...");
      setTimeout(() => {
        setShowForgot(false);
        setFpEmail("");
        setFpOtp("");
        setFpNewPassword("");
        setFpNewPasswordConfirm("");
        setFpStep(1);
        setFpSuccess("");
        setEmail("");
        setPassword("");
        setTotp("");
      }, 2000);
    } catch (err) {
      setFpError(err?.response?.data?.message || "Password reset failed. Please try again.");
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="h-screen flex justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl">
        {/* Logo */}
        <div className="flex justify-center mb-3">
          <img
            src="/logo.png"
            alt="ChatFlow Pro"
            className="h-16 sm:h-20 w-auto object-contain select-none"
          />
        </div>
        <p className="text-center text-gray-600 mb-8 text-sm font-medium">
          Professional WhatsApp Communication Platform
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm font-medium flex items-start">
              <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          </div>
        )}

        {/* Login Form */}
        <div className="space-y-4">
          {/* Email Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && login()}
              disabled={loggingIn || accountLocked}
              className="w-full border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Password Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && login()}
              disabled={loggingIn || accountLocked}
              className="w-full border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* 2FA Input */}
          {needsTotp && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Authenticator Code
              </label>
              <input
                type="text"
                placeholder="000000"
                maxLength="6"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyPress={(e) => e.key === "Enter" && login()}
                className="w-full border-2 border-blue-500 p-3 rounded-lg focus:border-blue-600 focus:outline-none transition text-center text-xl tracking-widest"
              />
              <p className="text-xs text-blue-600 mt-2 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                </svg>
                This is a new device. Enter the 6-digit code from Google Authenticator.
              </p>
            </div>
          )}

          {/* Sign In Button */}
          <button
            onClick={login}
            disabled={loggingIn || accountLocked}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-6"
          >
            {loggingIn && (
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            )}
            {loggingIn ? "Signing in..." : needsTotp ? "Verify & Sign In" : "Sign In"}
          </button>

          {/* Forgot Password Link */}
          <button
            onClick={() => {
              setShowForgot(true);
              setFpEmail(email);
              setFpError("");
              setFpSuccess("");
            }}
            className="w-full text-sm text-blue-600 font-semibold hover:text-blue-700 transition py-2"
          >
            Forgot password?
          </button>
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-500 text-center mt-6">
          🔒 Your login is secure and encrypted
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowForgot(false);
                setFpStep(1);
                setFpError("");
                setFpSuccess("");
              }}
              className="float-right text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-1 clear-right">
              Reset Password
            </h3>
            <p className="text-gray-600 mb-6 text-sm">
              {fpStep === 1 ? "Enter your email to receive a reset code" : "Enter the code and your new password"}
            </p>

            {/* Error Message */}
            {fpError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm font-medium flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {fpError}
                </p>
              </div>
            )}

            {/* Success Message */}
            {fpSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm font-medium flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {fpSuccess}
                </p>
              </div>
            )}

            {fpStep === 1 ? (
              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={fpEmail}
                  onChange={(e) => setFpEmail(e.target.value)}
                  disabled={fpLoading}
                  className="w-full border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowForgot(false);
                      setFpStep(1);
                    }}
                    className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendOtp}
                    disabled={fpLoading}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center"
                  >
                    {fpLoading && <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                    {fpLoading ? "Sending..." : "Send Code"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Reset Code (6 digits)</label>
                  <input
                    type="text"
                    placeholder="000000"
                    maxLength="6"
                    value={fpOtp}
                    onChange={(e) => setFpOtp(e.target.value.replace(/[^0-9]/g, ""))}
                    disabled={fpLoading}
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100 text-center text-lg tracking-widest"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={fpNewPassword}
                    onChange={(e) => setFpNewPassword(e.target.value)}
                    disabled={fpLoading}
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Must be at least 10 characters with uppercase, lowercase, number, and special character
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={fpNewPasswordConfirm}
                    onChange={(e) => setFpNewPasswordConfirm(e.target.value)}
                    disabled={fpLoading}
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:outline-none transition disabled:bg-gray-100"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setFpStep(1)}
                    disabled={fpLoading}
                    className="flex-1 bg-gray-100 py-3 rounded-lg font-semibold text-gray-700 hover:bg-gray-200 transition disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={resetPassword}
                    disabled={fpLoading}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center"
                  >
                    {fpLoading && <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
                    {fpLoading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
